import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sandwiches, miniPasteis, productCatalogInfo } from "@/mocks/products";
import ScrollReveal from "@/components/base/ScrollReveal";
import NutritionModal from "@/components/base/NutritionModal";
import type { Product } from "@/mocks/products";

export default function ProductCatalog() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"sanduiches" | "pasteis">("sanduiches");
  const [nutritionProduct, setNutritionProduct] = useState<Product | null>(null);

  const products = activeTab === "sanduiches" ? sandwiches : miniPasteis;
  const info = activeTab === "sanduiches"
    ? productCatalogInfo.categoryInfo["sanduiches-naturais"]
    : productCatalogInfo.categoryInfo["mini-pasteis"];

  return (
    <section id="catalogo" className="relative w-full py-20 md:py-28 bg-organic-cool overflow-hidden">
      <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
        <ScrollReveal className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            Nosso{" "}
            <span className="text-primary-600">catálogo</span>
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-base md:text-lg">
            Sanduíches naturais e mini pastéis preparados com ingredientes selecionados.
          </p>
          <p className="mt-4 inline-flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 bg-background-50 border border-background-200/50 rounded-full px-4 py-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-image-line text-primary-500"></i>
            </span>
            As fotos são meramente ilustrativas.
          </p>
        </ScrollReveal>

        {/* Tabs */}
        <ScrollReveal className="flex justify-center mb-10">
          <div className="inline-flex p-1 rounded-full bg-background-200/70 border border-background-300/50">
            <button
              onClick={() => setActiveTab("sanduiches")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === "sanduiches"
                  ? "bg-primary-500 text-background-50 shadow-md"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              Sanduíches Naturais
            </button>
            <button
              onClick={() => setActiveTab("pasteis")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === "pasteis"
                  ? "bg-primary-500 text-background-50 shadow-md"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              Mini Pastéis
            </button>
          </div>
        </ScrollReveal>

        {/* Category info */}
        <ScrollReveal className="flex flex-wrap items-center justify-center gap-3 mb-10 text-sm text-foreground-600">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/50">
            <i className="ri-scales-3-line text-primary-500"></i>
            Peso: {info.unitWeight}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/50">
            <i className="ri-coins-line text-primary-500"></i>
            Preco: R$ {info.unitPrice.toFixed(2)}
          </span>
          {info.packageSize && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/50">
              <i className="ri-archive-line text-primary-500"></i>
              {info.packageSize}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/50">
            <i className="ri-time-line text-primary-500"></i>
            Validade: {info.shelfLife}
          </span>
        </ScrollReveal>

        {/* Products Grid */}
        <ScrollReveal direction="up" delay={0.1} staggerChildren staggerDelay={0.08}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-16">
            {products.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/produto/${product.id}`)}
                className="group relative flex flex-col rounded-2xl bg-background-50 border border-background-200/30 card-glow transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer"
              >
                <div className="relative h-52 sm:h-56 img-zoom">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {product.badge && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold uppercase tracking-wider">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3 flex-1">
                    {product.shortDescription}
                  </p>
                  <div className="mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">Ingredientes:</span>
                    <p className="text-xs text-foreground-500 mt-1">
                      {product.ingredients.join(", ")}
                    </p>
                  </div>
                  {product.detailedNutrition && (
                    <div className="mb-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setNutritionProduct(product);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 border border-background-200/50 text-xs text-foreground-600 hover:text-primary-600 hover:border-primary-300/50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-file-list-3-line"></i>
                        </span>
                        Informações Nutricionais
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-background-200/50">
                    <div className="flex items-center gap-2 text-sm text-foreground-700">
                      <span className="font-semibold text-primary-600">R$ {product.price.toFixed(2)}</span>
                      <span className="text-foreground-400">· {product.weight}</span>
                    </div>
                    <a
                      href={`https://wa.me/558534942701?text=Olá! Gostaria de pedir ${encodeURIComponent(product.name)}.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line"></i>
                      Pedir
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal direction="up" delay={0.15}>
          <div className="text-center mt-10">
            <button
              onClick={() => navigate("/cardapio")}
              className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/20"
            >
              <i className="ri-restaurant-line text-lg"></i>
              Ver Cardápio completo
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
            </button>
          </div>
        </ScrollReveal>

        {/* Order Notes */}
        <ScrollReveal direction="up" delay={0.2} staggerChildren staggerDelay={0.06}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/30 card-glow transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-primary-500 text-lg"></i>
                <h4 className="font-heading font-semibold text-foreground-950">Disponibilidade</h4>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{productCatalogInfo.orderNotes.availability}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/30 card-glow transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-truck-line text-primary-500 text-lg"></i>
                <h4 className="font-heading font-semibold text-foreground-950">Entregas</h4>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{productCatalogInfo.orderNotes.delivery}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/30 card-glow transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-fridge-line text-primary-500 text-lg"></i>
                <h4 className="font-heading font-semibold text-foreground-950">Conservação</h4>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{productCatalogInfo.orderNotes.storage}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/30 card-glow transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-exchange-line text-primary-500 text-lg"></i>
                <h4 className="font-heading font-semibold text-foreground-950">Trocas</h4>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{productCatalogInfo.orderNotes.exchangePolicy}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/30 md:col-span-2 lg:col-span-2 card-glow transition-all duration-300 hover:-translate-y-0.5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-bank-card-line text-primary-500 text-lg"></i>
                <h4 className="font-heading font-semibold text-foreground-950">Formas de Pagamento</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {productCatalogInfo.orderNotes.paymentMethods.map((method) => (
                  <span key={method} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-500"></i>
                    {method}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {nutritionProduct && nutritionProduct.detailedNutrition && (
        <NutritionModal
          isOpen={true}
          onClose={() => setNutritionProduct(null)}
          data={nutritionProduct.detailedNutrition}
          productName={nutritionProduct.name}
        />
      )}
    </section>
  );
}